import { Link } from 'react-router-dom'
import { motion, useReducedMotion } from 'framer-motion'
import {
  ArrowRight,
  Bot,
  BrainCircuit,
  CalendarCheck,
  CheckCircle,
  ClipboardCheck,
  FileStack,
  HardHat,
  LineChart,
  ShieldCheck,
  Sparkles,
} from 'lucide-react'
import { APP_REGISTER_URL, PRODUCT_LINKS } from '../constants/nav'
import FadeIn from '../components/animations/FadeIn'
import StaggerContainer, { StaggerItem } from '../components/animations/StaggerContainer'

const platformSignals = [
  'Field execution',
  'Project controls',
  'Quality records',
  'Drawing truth',
  'Cost visibility',
  'AI insight',
]

const painPoints = [
  {
    title: 'Field work lives in one place',
    copy: 'Daily reports, safety, task completion, attendance, and equipment updates need to flow up without another spreadsheet.',
  },
  {
    title: 'Project controls live somewhere else',
    copy: 'Schedule, cost, procurement, RFIs, and owner issues only help when they stay connected to what actually happened in the field.',
  },
  {
    title: 'Quality and drawings decide the risk',
    copy: 'Wrong revisions, missed hold points, and scattered NCR records become expensive when nobody sees the pattern early.',
  },
]

const aiModes = [
  {
    icon: BrainCircuit,
    title: 'Morning risk brief',
    copy: 'The agent reads the project every morning and tells the PM what needs attention first.',
  },
  {
    icon: LineChart,
    title: 'What-if analysis',
    copy: 'Ask what happens if a delivery slips three weeks and get a plain-English schedule impact.',
  },
  {
    icon: ShieldCheck,
    title: 'Standards keeper',
    copy: 'Notice windows, commissioning requirements, and owner follow-ups surface before they become misses.',
  },
  {
    icon: Bot,
    title: 'Automated reporting',
    copy: 'Weekly owner reports pull from schedule, cost, RFIs, photos, field status, and open risks.',
  },
]

const productIcons = {
  Projects: CalendarCheck,
  Benchmark: ClipboardCheck,
  Design: FileStack,
  Pro: HardHat,
}

const projectFlow = [
  'Crew completes work in Pro',
  'Status updates PM controls',
  'Benchmark locks the quality record',
  'Drawings preserve revision context',
  'The AI agent watches the risk picture',
]

export default function Home() {
  const prefersReduced = useReducedMotion()

  const heroContainer = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: prefersReduced ? 0 : 0.12,
        delayChildren: prefersReduced ? 0 : 0.15,
      },
    },
  }

  const heroItem = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.25, 0.1, 0.25, 1] as const,
      },
    },
  }

  return (
    <>
      <section className="bg-navy pt-16 pb-12 sm:pt-20 sm:pb-16 overflow-hidden">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            variants={heroContainer}
            initial="hidden"
            animate="visible"
            className="grid lg:grid-cols-[1.05fr_0.95fr] gap-10 lg:gap-14 items-center"
          >
            <div>
              <motion.div variants={heroItem} className="mb-6">
                <span className="inline-flex items-center gap-2 border border-field-orange/60 text-field-orange rounded-full px-4 py-1.5 text-sm font-medium">
                  Connected construction project execution
                </span>
              </motion.div>

              <motion.h1 variants={heroItem} className="text-white text-4xl sm:text-5xl lg:text-[58px] font-heading font-semibold leading-[1.05] mb-6">
                Keep construction projects from drifting off track.
              </motion.h1>

              <motion.p variants={heroItem} className="text-white/70 text-lg sm:text-xl max-w-2xl mb-8 leading-relaxed">
                SiteDeck connects field operations, project controls, quality records, drawings,
                and AI project insight so teams catch problems before they compound.
              </motion.p>

              <motion.div variants={heroItem} className="flex flex-col sm:flex-row gap-4 mb-8">
                <Link to="/demo" className="btn-primary">
                  Book a Demo
                </Link>
                <a href={APP_REGISTER_URL} className="btn-ghost">
                  Try SiteDeck Pro Free
                </a>
              </motion.div>

              <motion.div variants={heroItem} className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {platformSignals.map((signal) => (
                  <div key={signal} className="flex items-center gap-2 text-white/70 text-sm">
                    <CheckCircle className="w-4 h-4 text-site-green shrink-0" />
                    {signal}
                  </div>
                ))}
              </motion.div>
            </div>

            <motion.div variants={heroItem} className="bg-white/8 border border-white/10 rounded-xl p-4 sm:p-5">
              <div className="bg-off-white rounded-lg overflow-hidden">
                <div className="bg-navy-mid px-4 py-3 flex items-center justify-between">
                  <p className="text-white text-sm font-semibold">Project risk brief</p>
                  <Sparkles className="w-4 h-4 text-field-orange" />
                </div>
                <div className="p-4 sm:p-5 space-y-4">
                  <div className="bg-white border border-gray-100 rounded-lg p-4">
                    <p className="section-label mb-2">AI insight</p>
                    <p className="text-navy font-heading font-semibold text-xl mb-2">
                      Electrical mobilization is at risk.
                    </p>
                    <p className="text-slate text-sm leading-relaxed">
                      Battery delivery moved 12 days. Civil turnover is still amber. Reschedule
                      crew arrival or the project burns standby cost next week.
                    </p>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    {[
                      ['Schedule', 'Amber'],
                      ['Quality', 'Green'],
                      ['Materials', 'Red'],
                    ].map(([label, status]) => (
                      <div key={label} className="bg-white border border-gray-100 rounded-lg p-3">
                        <p className="text-slate text-xs mb-1">{label}</p>
                        <p className="text-navy font-semibold">{status}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <section className="bg-off-white py-16 sm:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <p className="section-label mb-3">The problem</p>
            <h2 className="text-navy text-3xl sm:text-[42px] font-heading font-semibold leading-tight max-w-3xl mb-6">
              Projects rarely fail from one big event. They fail from disconnected signals.
            </h2>
            <p className="text-slate text-lg leading-relaxed max-w-3xl mb-10">
              A senior PM connects the civil delay, the delivery slip, the drawing revision, and
              the commissioning date from experience. SiteDeck connects those signals in the system.
            </p>
          </FadeIn>

          <StaggerContainer className="grid md:grid-cols-3 gap-5">
            {painPoints.map((item) => (
              <StaggerItem key={item.title}>
                <div className="bg-white border border-gray-100 rounded-xl p-6 h-full">
                  <div className="border-l-[3px] border-field-orange pl-4">
                    <h3 className="text-navy font-heading font-semibold mb-3">{item.title}</h3>
                    <p className="text-slate text-sm leading-relaxed">{item.copy}</p>
                  </div>
                </div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      <section id="features" className="bg-white py-16 sm:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <p className="section-label mb-3">One project layer</p>
            <h2 className="text-navy text-3xl sm:text-[42px] font-heading font-semibold leading-tight max-w-3xl mb-10">
              Linked tools around the same project.
            </h2>
          </FadeIn>

          <StaggerContainer className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {PRODUCT_LINKS.map((product) => {
              const Icon = productIcons[product.label as keyof typeof productIcons]
              return (
                <StaggerItem key={product.label}>
                  <Link to={product.href} className="block bg-white border border-gray-100 rounded-xl p-6 h-full hover:shadow-md transition-shadow">
                    <Icon className="w-6 h-6 text-field-orange mb-4" />
                    <div className="flex items-center justify-between gap-3 mb-2">
                      <h3 className="text-navy font-heading font-semibold">{product.label}</h3>
                      <span className="text-[11px] uppercase tracking-wide text-field-orange font-semibold">
                        {product.status}
                      </span>
                    </div>
                    <p className="text-slate text-sm leading-relaxed mb-4">{product.description}</p>
                    <span className="inline-flex items-center text-field-orange text-sm font-semibold">
                      View lane
                      <ArrowRight className="w-4 h-4 ml-1" />
                    </span>
                  </Link>
                </StaggerItem>
              )
            })}
          </StaggerContainer>
        </div>
      </section>

      <section className="bg-navy py-16 sm:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-[0.85fr_1.15fr] gap-10 lg:gap-14 items-start">
            <FadeIn>
              <p className="section-label mb-3">AI co-pilot</p>
              <h2 className="text-white text-3xl sm:text-[42px] font-heading font-semibold leading-tight mb-5">
                Not a chatbot. A senior-PM lens on your project.
              </h2>
              <p className="text-white/70 text-lg leading-relaxed">
                SiteDeck's agent reads across field status, schedule, cost, quality, drawings, and
                open issues to surface what needs attention before it turns into rework or delay.
              </p>
            </FadeIn>

            <StaggerContainer className="grid sm:grid-cols-2 gap-5">
              {aiModes.map((mode) => {
                const Icon = mode.icon
                return (
                  <StaggerItem key={mode.title}>
                    <div className="bg-white/8 border border-white/10 rounded-xl p-5 h-full">
                      <Icon className="w-6 h-6 text-field-orange mb-4" />
                      <h3 className="text-white font-heading font-semibold mb-2">{mode.title}</h3>
                      <p className="text-white/65 text-sm leading-relaxed">{mode.copy}</p>
                    </div>
                  </StaggerItem>
                )
              })}
            </StaggerContainer>
          </div>
        </div>
      </section>

      <section className="bg-off-white py-16 sm:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-6">
            <FadeIn>
              <div className="bg-white border border-gray-100 rounded-xl p-6 sm:p-8 h-full">
                <p className="section-label mb-3">Start free</p>
                <h2 className="text-navy text-2xl sm:text-[34px] font-heading font-semibold mb-4">
                  Start with SiteDeck Pro.
                </h2>
                <p className="text-slate leading-relaxed mb-6">
                  Small contractors can begin with field operations: crew, safety, daily reporting,
                  and team execution.
                </p>
                <a href={APP_REGISTER_URL} className="btn-primary">
                  Try SiteDeck Pro Free
                </a>
              </div>
            </FadeIn>

            <FadeIn delay={0.08}>
              <div className="bg-navy border border-navy rounded-xl p-6 sm:p-8 h-full">
                <p className="section-label mb-3">Platform demo</p>
                <h2 className="text-white text-2xl sm:text-[34px] font-heading font-semibold mb-4">
                  See the connected system.
                </h2>
                <p className="text-white/70 leading-relaxed mb-6">
                  Project teams can see how Pro, Projects, Benchmark, Design, and the AI co-pilot
                  work together around one project.
                </p>
                <Link to="/demo" className="btn-primary">
                  Book a Demo
                </Link>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      <section className="bg-white py-16 sm:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <div className="bg-off-white border border-gray-100 rounded-xl p-6 sm:p-10">
              <div className="grid lg:grid-cols-[0.9fr_1.1fr] gap-8 items-center">
                <div>
                  <p className="section-label mb-3">The ROI conversation</p>
                  <h2 className="text-navy text-3xl sm:text-[42px] font-heading font-semibold leading-tight">
                    One avoided rework event can pay for SiteDeck for years.
                  </h2>
                </div>
                <div className="space-y-3">
                  {projectFlow.map((step, index) => (
                    <div key={step} className="flex items-center gap-3 bg-white border border-gray-100 rounded-lg p-3">
                      <span className="w-7 h-7 rounded-full bg-field-orange text-white text-sm font-semibold flex items-center justify-center shrink-0">
                        {index + 1}
                      </span>
                      <p className="text-navy text-sm font-medium">{step}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>
    </>
  )
}
